import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

export function UomPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [newUnit, setNewUnit] = useState({ unitCode: '', factor: '' });
  const [conv, setConv] = useState({ quantity: '1', fromUnit: '', toUnit: '' });
  const [error, setError] = useState(null);
  const [convResult, setConvResult] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items) });
  const units = useQuery({ queryKey: ['inventory', 'product-units', productId], queryFn: () => inventoryApi.listProductUnits(productId).then((r) => r.items), enabled: Boolean(productId) });
  const unitList = units.data?.units || [];

  const add = useMutation({
    mutationFn: () => inventoryApi.addProductUnit(productId, { unitCode: newUnit.unitCode.toUpperCase(), factor: Number(newUnit.factor) }),
    onSuccess: () => { setError(null); setNewUnit({ unitCode: '', factor: '' }); queryClient.invalidateQueries({ queryKey: ['inventory', 'product-units', productId] }); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });

  const convert = useMutation({
    mutationFn: () => inventoryApi.convertUom(productId, { quantity: Number(conv.quantity), fromUnit: conv.fromUnit.toUpperCase(), toUnit: conv.toUnit.toUpperCase() }).then((r) => r.items),
    onSuccess: (d) => { setError(null); setConvResult(d); },
    onError: (e) => { setConvResult(null); setError(e?.message || t('common.loadError')); },
  });

  const columns = [
    { key: 'unit', label: t('uom.col.unit'), render: (r) => <span className="mono"><strong>{r.unitCode}</strong> {r.isBase && <Badge variant="info">{t('uom.base')}</Badge>}</span> },
    { key: 'factor', label: t('uom.col.factor'), align: 'right', render: (r) => <span className="mono num">{Number(r.factor).toString()}</span> },
    { key: 'meaning', label: '', render: (r) => <span className="muted text-sm">1 {r.unitCode} = {Number(r.factor).toString()} {units.data?.baseUnit}</span> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('uom.title')}</h1>
          <p className="muted">{t('uom.subtitle')}</p>
        </div>
        <div className="page-actions">
          <select className="input" style={{ maxWidth: 280 }} value={productId} onChange={(e) => { setProductId(e.target.value); setConvResult(null); setError(null); }}>
            <option value="">{t('uom.selectProduct')}</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </div>
      </header>

      {error && <div className="alert danger" style={{ marginBottom: 'var(--space-3)' }}>{error}</div>}

      {productId && (
        <>
          <Table columns={columns} rows={unitList} isLoading={units.isLoading} emptyMessage={t('uom.empty')} />

          <div className="card" style={{ padding: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
            <div className="settings-divider" style={{ marginTop: 0 }}>{t('uom.add')}</div>
            <div className="field-row" style={{ alignItems: 'flex-end' }}>
              <label className="field" style={{ maxWidth: 160 }}><span className="field-label">{t('uom.col.unit')}</span>
                <input className="input mono" value={newUnit.unitCode} onChange={(e) => setNewUnit((s) => ({ ...s, unitCode: e.target.value }))} placeholder="BOX" />
              </label>
              <label className="field" style={{ maxWidth: 180 }}><span className="field-label">{t('uom.factorLabel', { base: units.data?.baseUnit || '' })}</span>
                <input className="input num" type="number" min="0" step="any" value={newUnit.factor} onChange={(e) => setNewUnit((s) => ({ ...s, factor: e.target.value }))} />
              </label>
              <Button variant="primary" disabled={add.isPending || !newUnit.unitCode || !newUnit.factor} onClick={() => add.mutate()}>{t('uom.action.add')}</Button>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
            <div className="settings-divider" style={{ marginTop: 0 }}>{t('uom.converter')}</div>
            <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label className="field" style={{ maxWidth: 120 }}><span className="field-label">{t('uom.quantity')}</span>
                <input className="input num" type="number" min="0" step="any" value={conv.quantity} onChange={(e) => setConv((s) => ({ ...s, quantity: e.target.value }))} />
              </label>
              <label className="field" style={{ maxWidth: 130 }}><span className="field-label">{t('uom.from')}</span>
                <select className="input" value={conv.fromUnit} onChange={(e) => setConv((s) => ({ ...s, fromUnit: e.target.value }))}>
                  <option value="">-</option>
                  {unitList.map((u) => <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>)}
                </select>
              </label>
              <label className="field" style={{ maxWidth: 130 }}><span className="field-label">{t('uom.to')}</span>
                <select className="input" value={conv.toUnit} onChange={(e) => setConv((s) => ({ ...s, toUnit: e.target.value }))}>
                  <option value="">-</option>
                  {unitList.map((u) => <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>)}
                </select>
              </label>
              <Button variant="ghost" disabled={convert.isPending || !conv.fromUnit || !conv.toUnit} onClick={() => convert.mutate()}>{t('uom.action.convert')}</Button>
              {convResult && <span className="mono" style={{ marginLeft: 'var(--space-2)' }}>= <strong>{convResult.result} {convResult.toUnit}</strong></span>}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
